import React, { useCallback, useEffect, useState } from "react";
import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from "../misc/Card";
import { Label } from "../misc/Label";
import { Switch } from "../misc/Switch";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store/store";
import { Button } from "../../components/misc/Button";
import { getAuthHeader } from "../../utils/authHeader";
import axios from "axios";
import { NotifPrefsDTO } from "./dto/notif-prefs.dto";
import { toast } from "react-toastify";
import CustomToast from "../misc/CustomToast";
import { setUser } from "../../store/authSlice";
import { ArrowLeft, RefreshCw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useIsMobile } from "../../hooks/useIsMobile";
import { User } from "../../types/user";

interface Prefs {
    facebookNotifs: boolean;
    approveNotifs: boolean;
    weeklySummary: boolean;
}

// The weekly summary is on unless the user explicitly turned it off; the
// other two flags were always opt-in.
const prefsOf = (user: Partial<User> | null | undefined): Prefs => ({
    facebookNotifs: user?.prefFacebookNotify === true,
    approveNotifs: user?.prefPostApproval === true,
    weeklySummary: user?.prefWeeklySummary !== false,
});

interface RowProps {
    id: string;
    title: string;
    description: string;
    checked: boolean;
    onCheckedChange: (value: boolean) => void;
}

const PreferenceRow: React.FC<RowProps> = ({ id, title, description, checked, onCheckedChange }) => (
    <div className="flex items-center justify-between">
        <div className="flex flex-col flex-[0.8]">
            <Label htmlFor={id} className="font-semibold text-primary">
                {title}
            </Label>
            <span className="text-sm text-neutral-500 mt-2">{description}</span>
        </div>
        <Switch id={id} aria-label={title} checked={checked} onCheckedChange={onCheckedChange} />
    </div>
);

const NotificationPreferences = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const reduxUser = useSelector((state: RootState) => state.auth.user);
    const isMobile = useIsMobile(1200);
    const backendUrl = process.env.REACT_APP_BACKEND_URL;

    const [saved, setSaved] = useState<Prefs>(prefsOf(reduxUser));
    const [draft, setDraft] = useState<Prefs>(prefsOf(reduxUser));
    const [saving, setSaving] = useState(false);
    const [loaded, setLoaded] = useState(false);
    const [loadError, setLoadError] = useState(false);

    // Read the real values from the api: under impersonation the Redux user is
    // a placeholder with every preference false. Hold the switches back until
    // this resolves so the page never briefly shows the placeholder as real.
    const load = useCallback(async (): Promise<void> => {
        setLoadError(false);
        try {
            const headers = await getAuthHeader();
            const response = await axios.get<User>(`${backendUrl}/settings/personal-info`, { headers });
            const prefs = prefsOf(response.data);
            setSaved(prefs);
            setDraft(prefs);
            setLoaded(true);
        } catch (error) {
            console.error("Error loading notification preferences:", error);
            // A save sends all three flags, so leaving the rows up on a failed
            // load would let a click write the unconfirmed values over the
            // user's real ones. Show the retry instead.
            setLoaded(false);
            setLoadError(true);
            toast.error(
                <CustomToast message={"Error loading notification preferences"} type={"error"} />,
                { autoClose: 3000 }
            );
        }
    }, [backendUrl]);

    useEffect(() => {
        load();
    }, [load]);

    const changesMade =
        draft.facebookNotifs !== saved.facebookNotifs ||
        draft.approveNotifs !== saved.approveNotifs ||
        draft.weeklySummary !== saved.weeklySummary;

    const handleSubmit = async (): Promise<void> => {
        if (saving) return;
        setSaving(true);
        const data: NotifPrefsDTO = { ...draft };
        try {
            const headers = await getAuthHeader();
            await axios.patch(`${backendUrl}/settings/notifications`, data, { headers });
            const response = await axios.get<User>(`${backendUrl}/settings/personal-info`, { headers });
            dispatch(setUser(response.data));
            const prefs = prefsOf(response.data);
            setSaved(prefs);
            setDraft(prefs);
            toast.success(
                <CustomToast message={"Notification preferences updated"} type={"success"} />,
                { autoClose: 3000 }
            );
        } catch (error) {
            console.error("Error updating notification preferences:", error);
            toast.error(
                <CustomToast message={"Error updating notification preferences"} type={"error"} />,
                { autoClose: 3000 }
            );
        } finally {
            setSaving(false);
        }
    };

    const set = (key: keyof Prefs) => (value: boolean): void =>
        setDraft((prev) => ({ ...prev, [key]: value }));

    return (
        <Card className={`w-full max-w-4xl mx-auto ${isMobile ? "h-full m-0 rounded-none" : "h-auto"}`}>
            <div className="p-4">
                <Button id="back-to-settings-btn" onClick={() => navigate("/settings")} className="mb-4 flex items-center">
                    <ArrowLeft className="mr-2 h-5 w-5" /> Back to Settings
                </Button>
            </div>
            <CardHeader className="mb-0 pb-0">
                <CardTitle>Notification Preferences</CardTitle>
                <CardDescription>Manage your notification preferences</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
                {loadError && (
                    <div className="flex flex-col items-center py-10 text-center">
                        <h2 className="text-lg font-semibold text-primary">
                            We couldn't load your notification preferences
                        </h2>
                        <p className="mt-1 max-w-md text-sm text-gray-500">
                            Your saved settings were not read, so they are hidden to keep a save from
                            overwriting them. Try again in a moment.
                        </p>
                        <Button id="retry-load-btn" onClick={load} variant="secondary" className="mt-4">
                            <RefreshCw className="h-4 w-4" />
                            Try again
                        </Button>
                    </div>
                )}
                {loaded && (
                    <>
                        <PreferenceRow
                            id="facebook-notifications"
                            title="Facebook Post Notifications"
                            description="This option will notify you via email whenever a Facebook post is published to your business profile, allowing you to quickly share it on your personal page."
                            checked={draft.facebookNotifs}
                            onCheckedChange={set("facebookNotifs")}
                        />
                        <PreferenceRow
                            id="approve-notifications"
                            title="Unapproved Post Notifications"
                            description="This option will notify you via email whenever an unapproved post is found on your profile, reminding you to approve it."
                            checked={draft.approveNotifs}
                            onCheckedChange={set("approveNotifs")}
                        />
                        <PreferenceRow
                            id="weekly-summary"
                            title="Weekly Activity Summary"
                            description="A weekly email summarizing what Marabot published for you over the past seven days, with a link to your analytics."
                            checked={draft.weeklySummary}
                            onCheckedChange={set("weeklySummary")}
                        />
                    </>
                )}
            </CardContent>
            <div className="flex justify-center margin mb-4">
                {loaded && changesMade && (
                    <Button id="save-changes-btn" onClick={handleSubmit} disabled={saving}>
                        {saving ? "Saving..." : "Save Changes"}
                    </Button>
                )}
            </div>
        </Card>
    );
};

export default NotificationPreferences;
